import React, { useState, useEffect } from "react";
import Navbar from "./Navbar";
import EFTReceipt from "./EFTReceipt";
import toast from "react-hot-toast";
import {
  checkCommissionTrustEFT,
  getEFTTypeDisplayName,
  formatDate,
  getPartyNamesByDealType,
} from "../utils/eftUtils";
import {
  getNetToOfficeTotal,
  getOurBrokerageEftAmount,
  getTotalBuyerRebateAmount,
} from "../utils/tradeFinancialUtils";
import axiosInstance from "../config/axios";
import { getTradeTaxRate, INCLUSIVE_HST_FRACTION } from "../constants/tax";
import { normalizeThroughDateForApi } from "../utils/agentCommissionYtd";
import { officeExpensesSplitForAgent } from "../utils/officeExpenses";

/**
 * Trade-row aggregates and payment amounts for one agent.
 * `netCommission` on the trade is the agent’s true net (Amount+Tax−fees, and buyer rebate is deducted in Net on save).
 * `netPayAmount` = EFT/cheque = that stored net (rebate is already reflected in netCommission).
 * `preRebateGross` ex-HST = sum of Amount column(s) (full commission; rebate is separate).
 * Aligns with backend `aggregateAgentCommissionForEft`, YTD, and EFT receipt rows.
 */
function getAgentCommissionEftBreakdown(trade, agentId) {
  const empty = {
    netCommission: 0,
    netPayAmount: 0,
    agentAmountSum: 0,
    officePlanAmount: 0,
    officeExpensesAmount: 0,
    commissionDeductionAmount: 0,
    buyerRebateTotal: 0,
    preRebateGross: 0,
  };
  if (!trade || agentId == null || agentId === "") {
    return empty;
  }
  const taxRate = getTradeTaxRate(trade);
  const sumOfficeSplits = (rows) =>
    rows.reduce(
      (acc, a) => {
        const split = officeExpensesSplitForAgent(a, taxRate);
        acc.officeExpensesAmount += split.officeExpensesAmount;
        acc.commissionDeductionAmount += split.commissionDeductionAmount;
        return acc;
      },
      { officeExpensesAmount: 0, commissionDeductionAmount: 0 },
    );

  const isDouble = trade.keyInfo?.classification === "AGENT DOUBLE ENDER";
  if (isDouble) {
    const agentTransactions =
      trade.agentCommissionList?.filter(
        (a) =>
          a.agentId === agentId && a.classification === "AGENT DOUBLE ENDER",
      ) || [];
    const netCommission = agentTransactions.reduce(
      (sum, a) => sum + parseFloat(a.netCommission || 0),
      0,
    );
    const agentAmountSum = agentTransactions.reduce(
      (sum, a) => sum + parseFloat(a.amount || 0),
      0,
    );
    const officePlanAmount = agentTransactions.reduce(
      (sum, a) => sum + parseFloat(a.feesDeducted || 0),
      0,
    );
    const { officeExpensesAmount, commissionDeductionAmount } =
      sumOfficeSplits(agentTransactions);
    const buyerRebateTotal = agentTransactions.reduce((sum, a) => {
      if (
        String(a.buyerRebateIncluded || "").toLowerCase() === "yes" &&
        a.buyerRebateAmount
      ) {
        return sum + (parseFloat(a.buyerRebateAmount) || 0);
      }
      return sum;
    }, 0);
    const preRebateGross = Math.round(agentAmountSum * 100) / 100;
    const netPayAmount = Math.max(
      0,
      Math.round(Number(netCommission) * 100) / 100,
    );
    return {
      netCommission,
      netPayAmount,
      agentAmountSum,
      officePlanAmount,
      officeExpensesAmount: Math.round(officeExpensesAmount * 100) / 100,
      commissionDeductionAmount:
        Math.round(commissionDeductionAmount * 100) / 100,
      buyerRebateTotal,
      preRebateGross,
    };
  }
  const selectedAgent =
    trade.agentCommissionList?.find((a) => a.agentId === agentId) || {};
  const netCommission = parseFloat(selectedAgent.netCommission || 0);
  const agentAmountSum = parseFloat(selectedAgent.amount || 0);
  const officePlanAmount = parseFloat(selectedAgent.feesDeducted || 0);
  const split = officeExpensesSplitForAgent(selectedAgent, taxRate);
  const officeExpensesAmount = split.officeExpensesAmount;
  const commissionDeductionAmount = split.commissionDeductionAmount;
  let buyerRebateTotal = 0;
  if (
    String(selectedAgent.buyerRebateIncluded || "").toLowerCase() === "yes" &&
    selectedAgent.buyerRebateAmount
  ) {
    buyerRebateTotal = parseFloat(selectedAgent.buyerRebateAmount) || 0;
  }
  const preRebateGross = Math.round(agentAmountSum * 100) / 100;
  const netPayAmount = Math.max(
    0,
    Math.round(Number(netCommission) * 100) / 100,
  );
  return {
    netCommission,
    netPayAmount,
    agentAmountSum,
    officePlanAmount,
    officeExpensesAmount,
    commissionDeductionAmount,
    buyerRebateTotal,
    preRebateGross,
  };
}

const CommissionTrustPayments = () => {
  const [activeSection, setActiveSection] = useState("outside-brokers");
  const [trades, setTrades] = useState([]);
  const [filteredTrades, setFilteredTrades] = useState([]);
  const [showTradeDropdown, setShowTradeDropdown] = useState(false);
  const [showDetailsForm, setShowDetailsForm] = useState(false);
  const [showEFTReceipt, setShowEFTReceipt] = useState(false);
  const [eftNumber, setEftNumber] = useState(null);
  const [eftReceiptData, setEftReceiptData] = useState(null);
  const [isOurBrokerageEFT, setIsOurBrokerageEFT] = useState(false);

  // Add state to track EFT creation status
  // For agentCommission, track per agent using agentId as key
  // For outsideBrokersCommission, track per broker using "Name - Company" as key
  const [eftCreated, setEftCreated] = useState({
    agentCommission: {}, // Object with agentId as keys: { "100": true, "101": false }
    outsideBrokersCommission: {}, // { "Frank Henry - HomeLife Future Realty Inc": true, ... }
    ourBrokerageCommission: false,
  });

  // Agent Commission form state
  const [agentCommissionForm, setAgentCommissionForm] = useState({
    name: "Agent Commission transfer form",
    chequeType: "lorem ipsum",
    tradeNumber: "",
    selectedTrade: null,
    selectedAgentId: "",
  });

  // Outside Brokers Commission form state
  const [outsideBrokersForm, setOutsideBrokersForm] = useState({
    name: "Outside Brokers Commission Transfer Form",
    type: "EFT",
    tradeNumber: "",
    selectedTrade: null,
    selectedOutsideBroker: null,
  });

  // Outside Brokers Commission details form state
  const [showOutsideBrokersDetailsForm, setShowOutsideBrokersDetailsForm] =
    useState(false);
  const [outsideBrokersDetailsForm, setOutsideBrokersDetailsForm] = useState({
    chequeDate: new Date().toISOString().split("T")[0],
    bankAccount: "Commission Trust",
    outsideBrokerName: "",
    outsideBrokerCompany: "",
    tradeNumber: "",
    address: "",
    netCommission: "",
    netCheque: "",
    noteOnStub: "Transfer commission to outside broker",
  });
  const [showOutsideBrokersReceipt, setShowOutsideBrokersReceipt] =
    useState(false);
  const [outsideBrokersReceiptData, setOutsideBrokersReceiptData] =
    useState(null);

  // Our Brokerage Commission form state
  const [ourBrokerageForm, setOurBrokerageForm] = useState({
    tradeNumber: "",
    selectedTrade: null,
  });
  const [showOurBrokerageDetailsForm, setShowOurBrokerageDetailsForm] =
    useState(false);

  // Builder Repayment form state
  const [builderRepaymentForm, setBuilderRepaymentForm] = useState({
    name: "Builder Repayment Transfer Form",
    type: "EFT",
    tradeNumber: "",
    selectedTrade: null,
    builderName: "",
    suggestedRepaymentAmount: null,
  });
  const [showBuilderRepaymentDetailsForm, setShowBuilderRepaymentDetailsForm] =
    useState(false);
  const [builderRepaymentEftCreated, setBuilderRepaymentEftCreated] =
    useState(false);

  // General to Commission transfer form state
  const [generalToCommissionForm, setGeneralToCommissionForm] = useState({
    tradeNumber: "",
    selectedTrade: null,
  });
  const [
    showGeneralToCommissionDetailsForm,
    setShowGeneralToCommissionDetailsForm,
  ] = useState(false);
  const [generalToCommissionEftCreated, setGeneralToCommissionEftCreated] =
    useState(false);

  // Fetch trades on component mount
  useEffect(() => {
    fetchTrades();
  }, []);

  const fetchTrades = async () => {
    try {
      const response = await axiosInstance.get("/trades");
      setTrades(response.data);
    } catch (error) {
      console.error("Error fetching trades:", error);
    }
  };

  /** Filter trades by number/address; safe when tradeNumber is null. */
  const filterTradesBySearch = (searchTerm) => {
    const q = String(searchTerm ?? "").trim();
    if (!q) return [];
    const qLower = q.toLowerCase();
    return (trades || []).filter((trade) => {
      if (!trade) return false;
      const tn = String(
        trade.tradeNumber ?? trade.keyInfo?.tradeNumber ?? "",
      );
      const address = `${trade.keyInfo?.streetNumber || ""} ${
        trade.keyInfo?.streetName || ""
      }${trade.keyInfo?.unit ? ` ${trade.keyInfo.unit}` : ""}`.toLowerCase();
      return tn.includes(q) || address.includes(qLower);
    });
  };

  // Handle trade number search for outside brokers
  const handleOutsideBrokersTradeSearch = (searchTerm) => {
    const q = String(searchTerm ?? "");
    setOutsideBrokersForm((prev) => ({
      ...prev,
      tradeNumber: q,
      selectedTrade: null,
      selectedOutsideBroker: null,
    }));

    if (q.trim() === "") {
      setFilteredTrades([]);
      setShowTradeDropdown(false);
      return;
    }

    setFilteredTrades(filterTradesBySearch(q));
    setShowTradeDropdown(true);
  };

  // Build a stable key for an outside broker (must match recipient format used when creating EFT)
  // For referral agents, use agent name as company so EFT recipient/key is "Name - Name"
  const getOutsideBrokerKey = (broker) => {
    if (!broker) return "";
    const name =
      broker.agentName ||
      `${broker.firstName || ""} ${broker.lastName || ""}`.trim();
    if (broker._isReferral || broker.company === "Referral") {
      return `${name} - ${name}`;
    }
    return broker.company ? `${name} - ${broker.company}` : name;
  };

  // Combined list for dropdown: if trade has referral agents, show only referral agents; otherwise outsideBrokers + referral agents
  const getOutsideBrokersWithReferrals = (trade) => {
    const outsideBrokers = trade?.outsideBrokers || [];
    const referralAgents = (trade?.agentCommissionList || []).filter(
      (a) => (a.classification || "").toUpperCase() === "REFERRAL",
    );
    const referralAsBrokers = referralAgents.map((a) => {
      const parts = (a.agentName || "").trim().split(/\s+/);
      return {
        _isReferral: true,
        agentName: a.agentName || "",
        agentId: a.agentId,
        company: "Referral",
        firstName: parts[0] || "",
        lastName: parts.slice(1).join(" ") || "",
        amount: a.amount,
        netCommission: a.netCommission,
        streetNumber: a.streetNumber || "",
        streetName: a.streetName || "",
        unitNumber: a.unitNumber || "",
        province: a.province || "",
        postalCode: a.postalCode || "",
        address: a.address || "",
        email: a.email || "",
        primaryPhone: a.primaryPhone || "",
      };
    });
    if (referralAgents.length > 0) {
      return referralAsBrokers;
    }
    return [...outsideBrokers, ...referralAsBrokers];
  };

  // Select trade from dropdown for outside brokers
  const selectOutsideBrokersTrade = async (trade) => {
    const address = `${trade.keyInfo?.streetNumber || ""} ${
      trade.keyInfo?.streetName || ""
    }${trade.keyInfo?.unit ? ` ${trade.keyInfo.unit}` : ""}`.trim();
    try {
      const response = await axiosInstance.get(`/trades/${trade._id}`);
      const fullTrade = response.data;

      // Check which outside brokers and referral agents already have an EFT for this trade
      let brokerEftStatus = {};
      try {
        const checkUrl = `/commission-trust-eft/check-existing/${trade._id}/OutsideBrokerCommission`;
        const checkRes = await axiosInstance.get(checkUrl);
        const recipients = checkRes.data?.recipients || [];
        const allBrokers = getOutsideBrokersWithReferrals(fullTrade);
        allBrokers.forEach((broker) => {
          const key = getOutsideBrokerKey(broker);
          brokerEftStatus[key] = recipients.includes(key);
        });
      } catch (checkErr) {
        console.error("Error checking existing outside broker EFTs:", checkErr);
      }

      setEftCreated((prev) => ({
        ...prev,
        outsideBrokersCommission: brokerEftStatus,
      }));
      setOutsideBrokersForm((prev) => ({
        ...prev,
        tradeNumber: `${trade.tradeNumber} - ${address}`,
        selectedTrade: fullTrade,
        selectedOutsideBroker: null, // Reset outside broker selection
      }));
      setShowTradeDropdown(false);
    } catch (error) {
      console.error("Error fetching full trade details:", error);
      toast.error("Could not load trade details. Please try again.");
    }
  };

  // Handle outside brokers form submission
  const handleOutsideBrokersSubmit = (e) => {
    e.preventDefault();
    if (
      outsideBrokersForm.selectedTrade &&
      outsideBrokersForm.selectedOutsideBroker
    ) {
      setShowOutsideBrokersDetailsForm(true);
    } else if (!outsideBrokersForm.selectedTrade) {
      alert("Please select a trade to proceed.");
    } else {
      alert("Please select an outside broker to proceed.");
    }
  };

  // Handle trade number search for agent commission
  const handleAgentCommissionTradeSearch = (searchTerm) => {
    const q = String(searchTerm ?? "");
    setAgentCommissionForm((prev) => ({
      ...prev,
      tradeNumber: q,
      selectedTrade: null,
      selectedAgentId: "",
    }));

    if (q.trim() === "") {
      setFilteredTrades([]);
      setShowTradeDropdown(false);
      return;
    }

    setFilteredTrades(filterTradesBySearch(q));
    setShowTradeDropdown(true);
  };

  // Select trade from dropdown for agent commission
  const selectAgentCommissionTrade = async (trade) => {
    const address = `${trade.keyInfo?.streetNumber || ""} ${
      trade.keyInfo?.streetName || ""
    }${trade.keyInfo?.unit ? ` ${trade.keyInfo.unit}` : ""}`.trim();
    try {
      const response = await axiosInstance.get(`/trades/${trade._id}`);
      const fullTrade = response.data;

      // Check EFT status for each agent in the trade
      const agentEftStatus = {};
      if (
        fullTrade.agentCommissionList &&
        fullTrade.agentCommissionList.length > 0
      ) {
        // Get unique agent IDs
        const uniqueAgentIds = [
          ...new Set(
            fullTrade.agentCommissionList.map((agent) => agent.agentId),
          ),
        ];

        // Check EFT status for each unique agent
        for (const agentId of uniqueAgentIds) {
          const existingEFT = await checkCommissionTrustEFT(
            trade._id,
            "AgentCommissionTransfer",
            agentId,
          );
          agentEftStatus[agentId] = existingEFT.exists;
        }
      }

      setEftCreated((prev) => ({
        ...prev,
        agentCommission: agentEftStatus,
      }));

      setAgentCommissionForm((prev) => ({
        ...prev,
        tradeNumber: `${trade.tradeNumber} - ${address}`,
        selectedTrade: fullTrade,
        selectedAgentId: "", // Reset agent selection
      }));
      setShowTradeDropdown(false);
    } catch (error) {
      console.error("Error fetching full trade details:", error);
      toast.error("Could not load trade details. Please try again.");
    }
  };

  // Handle agent commission form submission
  const handleAgentCommissionSubmit = (e) => {
    e.preventDefault();
    if (
      agentCommissionForm.selectedTrade &&
      agentCommissionForm.selectedAgentId
    ) {
      setShowDetailsForm(true);
    } else if (!agentCommissionForm.selectedTrade) {
      alert("Please select a trade to proceed.");
    } else {
      alert("Please select an agent to proceed.");
    }
  };

  const handleAgentCommissionEFTClick = async (formState) => {
    if (
      !agentCommissionForm.selectedTrade ||
      !agentCommissionForm.selectedAgentId
    ) {
      alert("No trade or agent selected.");
      return;
    }
    const trade = agentCommissionForm.selectedTrade;

    const selectedAgent = trade.agentCommissionList?.find(
      (agent) => agent.agentId === agentCommissionForm.selectedAgentId,
    );

    if (!selectedAgent) {
      alert("Selected agent not found.");
      return;
    }

    // Check if EFT already exists for this specific agent
    const existingEFT = await checkCommissionTrustEFT(
      trade._id,
      "AgentCommissionTransfer",
      selectedAgent.agentId,
    );

    // Only block if EFT exists AND the agentId matches (to handle edge cases where old EFTs might not have agentId)
    if (existingEFT.exists) {
      // Verify that the found EFT is actually for this agent
      // If agentId doesn't match, it might be an old EFT without proper agentId, so allow creation
      if (
        existingEFT.agentId &&
        existingEFT.agentId !== selectedAgent.agentId
      ) {
        console.warn(
          `Found EFT #${existingEFT.eftNumber} but agentId mismatch. Expected: ${selectedAgent.agentId}, Found: ${existingEFT.agentId}. Allowing creation.`,
        );
        // Continue with EFT creation
      } else if (existingEFT.agentId === selectedAgent.agentId) {
        // EFT exists for this exact agent, block creation
        toast.error(
          `Payment already completed for Agent ${selectedAgent.agentName} (${selectedAgent.agentId}) on Trade #${trade.tradeNumber}! 
          EFT #${existingEFT.eftNumber} was created on ${formatDate(
            existingEFT.date,
          )} 
          for ${getEFTTypeDisplayName(existingEFT.type)}.`,
          { duration: 6000 },
        );
        return;
      }
      // If agentId is null/undefined in existing EFT, allow creation (might be old record)
    }

    try {
      console.log("Selected agent:", selectedAgent);
      console.log("Agent ID:", selectedAgent.agentId);

      // 1. Fetch Company Profile (optional - use default if not available)
      console.log("Fetching company profile...");
      let companyInfo = null;
      try {
        const companyProfileRes = await axiosInstance.get("/company-profile");
        companyInfo = companyProfileRes.data;
        console.log("Company info:", companyInfo);
      } catch (companyError) {
        if (companyError.response?.status === 404) {
          console.log("Company profile not found, using default values");
          // Use default company info if profile doesn't exist
          companyInfo = {
            companyName: "ABC Realty Inc., Brokerage",
            address: "634 Champlain St",
            city: "Dieppe",
            province: "NB",
            postalCode: "E1A 1P4",
            phone: "123 456 7890",
            fax: "123 456 7891",
            email: "email@company.com",
          };
        } else {
          throw companyError;
        }
      }

      // 2. Fetch Agent's full details for address
      console.log(
        "Fetching agent details for employee number:",
        selectedAgent.agentId,
      );
      const agentDetailsRes = await axiosInstance.get(
        `/agents/employee/${selectedAgent.agentId}`,
      );
      const agentDetails = agentDetailsRes.data;
      console.log("Agent details:", agentDetails);

      // 3. Create EFT record in the database (net + gross/rebate/office — one source of truth)
      const {
        netCommission,
        netPayAmount,
        officePlanAmount,
        officeExpensesAmount,
        commissionDeductionAmount,
        buyerRebateTotal,
        preRebateGross,
      } = getAgentCommissionEftBreakdown(trade, selectedAgent.agentId);
      console.log(
        "Creating EFT for amount (net of buyer rebate):",
        netPayAmount,
      );
      const eftResponse = await axiosInstance.post(
        "/commission-trust-eft/agent-commission",
        {
          tradeId: trade._id,
          amount: netPayAmount,
          recipient: selectedAgent.agentName,
          agentId: selectedAgent.agentId,
          agentName: selectedAgent.agentName,
          chequeDate: formState.chequeDate,
        },
      );
      const newEftNumber = eftResponse.data.eftNumber;
      console.log("EFT created with number:", newEftNumber);

      // 4. Post transaction - Credit to 10003 Cash - Commission Trust Account, debit commission expense
      console.log("Posting transaction...");
      await axiosInstance.post("/transactions", {
        date: (() => {
          const [month, day, year] = formState.chequeDate.split("/");
          return new Date(
            `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}T00:00:00`,
          );
        })(),
        reference: `EFT#${newEftNumber}`,
        description: "Agent Commission Payment",
        debitAccount: "A/P - GENERAL & COMMISSION EXPENSE", // Debit commission expense account
        creditAccount: "CASH - COMMISSION TRUST ACCOUNT", // Credit to 10003
        amount: netPayAmount,
        tradeId: trade._id,
      });

      await axiosInstance.post("/finance-transactions", {
        type: "AgentCommissionPayment",
        chequeDate: (() => {
          const [month, day, year] = formState.chequeDate.split("/");
          return new Date(
            `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}T00:00:00`,
          );
        })(),
        amount: netPayAmount,
        chequeWrittenTo: selectedAgent.agentName,
        tradeId: trade._id,
      });

      // 5. Prepare data for the receipt
      const partyNames = getPartyNamesByDealType(trade);
      const sellers = partyNames.firstParty;
      const buyers = partyNames.secondParty;
      const tradeAddress = `${trade.keyInfo?.streetNumber || ""} ${
        trade.keyInfo?.streetName || ""
      }${trade.keyInfo?.unit ? ` ${trade.keyInfo.unit}` : ""}`.trim();

      // Build the address string from agent details
      const agentAddress = `${agentDetails.streetNumber || ""} ${
        agentDetails.streetName || ""
      }`.trim();
      const agentUnit = agentDetails.unit ? ` Unit ${agentDetails.unit}` : "";
      const agentCityProvincePostal = `${agentDetails.city || ""}, ${
        agentDetails.province || ""
      }, ${agentDetails.postalCode || ""}`.trim();

      // EFT summary: HST = tax rate × (gross ex-HST − office plan), same as Financial sheet agent row (not inclusive extract on net pay).
      const tradeCommissionExHST = preRebateGross;
      const commissionAfterPlanExHST = Math.max(
        0,
        Math.round((preRebateGross - officePlanAmount) * 100) / 100,
      );
      const hstOnAgentShare =
        commissionAfterPlanExHST > 0
          ? Math.round(
              commissionAfterPlanExHST * getTradeTaxRate(trade) * 100,
            ) / 100
          : Math.round(netPayAmount * INCLUSIVE_HST_FRACTION * 100) / 100;
      const grossEarningsExHST = preRebateGross;

      let ytdForSummary = {
        ytdGrossEarningsExHST: null,
        ytdOfficeShareLess: null,
        ytdOfficeExpenses: null,
        ytdCommissionDeduction: null,
        ytdBuyerRebate: null,
        ytdHstOnAgentShare: null,
        ytdNetPay: null,
      };
      try {
        const ytdAgentKey =
          agentDetails?.employeeNo != null && agentDetails.employeeNo !== ""
            ? String(agentDetails.employeeNo)
            : String(selectedAgent.agentId ?? "");
        const ytdRes = await axiosInstance.get(
          `/commission-trust-eft/agent-ytd/${encodeURIComponent(ytdAgentKey)}`,
          {
            params: {
              throughDate: normalizeThroughDateForApi(formState.chequeDate),
            },
          },
        );
        ytdForSummary = {
          ytdGrossEarningsExHST: ytdRes.data?.ytdGrossEarningsExHST ?? null,
          ytdOfficeShareLess: ytdRes.data?.ytdOfficeShareLess ?? null,
          ytdOfficeExpenses: ytdRes.data?.ytdOfficeExpenses ?? null,
          ytdCommissionDeduction: ytdRes.data?.ytdCommissionDeduction ?? null,
          ytdBuyerRebate: ytdRes.data?.ytdBuyerRebate ?? null,
          ytdHstOnAgentShare: ytdRes.data?.ytdHstOnAgentShare ?? null,
          ytdNetPay: ytdRes.data?.ytdNetPay ?? null,
        };
      } catch (ytdErr) {
        console.warn("Could not load YTD totals for EFT receipt:", ytdErr);
      }

      const dataForReceipt = {
        companyInfo: companyInfo,
        eftNumber: newEftNumber,
        paidTo: {
          name: selectedAgent.agentName,
          address: agentAddress,
        },
        trade: tradeAddress,
        notes:
          buyerRebateTotal > 0
            ? "Transfer commission to agent (net of buyer rebate)"
            : "Transfer commission to agent",
        amount: netPayAmount,
        seller: sellers,
        buyer: buyers,
        firstPartyLabel: partyNames.firstPartyLabel,
        secondPartyLabel: partyNames.secondPartyLabel,
        payTo: selectedAgent.agentName,
        orderOf: {
          address: `${agentAddress}${agentUnit}`,
          cityProvincePostal: agentCityProvincePostal,
        },
        note: `${trade.tradeNumber} ${tradeAddress}`,
        chequeDate: formState.chequeDate,
        eftAgentSummary: {
          agentNumber: String(selectedAgent.agentId ?? ""),
          agentName: selectedAgent.agentName ?? "",
          tradeNumber: trade.tradeNumber ?? "",
          propertyAddress: tradeAddress,
          tradeCommissionExHST,
          grossEarningsExHST,
          officeShareLess: officePlanAmount,
          officeExpensesAmount,
          commissionDeductionAmount,
          buyerRebateAmount: buyerRebateTotal,
          hstOnAgentShare,
          netPay: netPayAmount,
          ...ytdForSummary,
        },
      };

      setEftReceiptData(dataForReceipt);
      setIsOurBrokerageEFT(false);
      setShowEFTReceipt(true);
      // Set EFT as created for this specific agent to disable the button
      setEftCreated((prev) => ({
        ...prev,
        agentCommission: {
          ...prev.agentCommission,
          [selectedAgent.agentId]: true,
        },
      }));
      toast.success(
        "Transaction posted to Chart of Accounts and Ledger successfully!",
      );
    } catch (error) {
      console.error("Error during EFT process:", error);
      console.error("Error response:", error.response?.data);
      console.error("Error status:", error.response?.status);

      // Provide more specific error messages based on the error
      if (error.response?.status === 404) {
        toast.error(
          "Required data not found. Please ensure all required information is set up.",
        );
      } else {
        const serverMsg =
          error.response?.data?.error ||
          error.response?.data?.message ||
          error.message;
        toast.error(
          serverMsg
            ? `Failed to complete EFT process: ${serverMsg}`
            : "Failed to complete EFT process. Please try again.",
        );
      }
    }
  };

  // Helper for mm/dd/yyyy format
  const getCurrentMDYDate = () => {
    const now = new Date();
    const pad = (n) => n.toString().padStart(2, "0");
    return `${pad(now.getMonth() + 1)}/${pad(
      now.getDate(),
    )}/${now.getFullYear()}`;
  };

  // Agent Commission Details Form Component
  const AgentCommissionDetailsForm = ({
    trade,
    selectedAgentId,
    onBack,
    eftCreatedForAgent,
  }) => {
    const [formState, setFormState] = useState({
      chequeDate: getCurrentMDYDate(),
      bankAccount: "Commission Trust",
      agentNumber: "",
      agentName: "",
      tradeNumber: "",
      address: "",
      netCommission: "",
      netCheque: "",
      noteOnStub: "Transfer commission to agent",
    });

    const [useCustomDate, setUseCustomDate] = useState(false);

    useEffect(() => {
      if (trade && selectedAgentId) {
        const selectedAgent =
          trade.agentCommissionList?.find(
            (agent) => agent.agentId === selectedAgentId,
          ) || {};
        const b = getAgentCommissionEftBreakdown(trade, selectedAgentId);
        const address = `${trade.keyInfo?.streetNumber || ""} ${
          trade.keyInfo?.streetName || ""
        }${trade.keyInfo?.unit ? ` ${trade.keyInfo.unit}` : ""}`.trim();

        setFormState({
          chequeDate: getCurrentMDYDate(),
          bankAccount: "Commission Trust",
          agentNumber: selectedAgent.agentId || "",
          agentName: selectedAgent.agentName || "",
          tradeNumber: trade.tradeNumber,
          address: address,
          netCommission: b.netCommission.toFixed(2),
          netCheque: b.netPayAmount.toFixed(2),
          noteOnStub:
            b.buyerRebateTotal > 0
              ? "Transfer commission to agent (net of buyer rebate)"
              : "Transfer commission to agent",
        });
      }
    }, [trade, selectedAgentId]);

    const handleInputChange = (e) => {
      const { name, value } = e.target;
      setFormState((prev) => ({
        ...prev,
        [name]: value,
      }));
    };

    return (
      <div className="p-6 border border-gray-200 rounded-lg">
        <h3 className="font-semibold mb-4 text-lg">
          Agent Commission Cheque Details
        </h3>

        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Cheque Date
            </label>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="useCustomDate"
                checked={useCustomDate}
                onChange={(e) => setUseCustomDate(e.target.checked)}
                className="rounded border-gray-300"
              />
              <label htmlFor="useCustomDate" className="text-sm text-gray-600">
                Override Date
              </label>
            </div>
            {useCustomDate ? (
              <input
                type="date"
                name="chequeDate"
                value={
                  formState.chequeDate.includes("/")
                    ? (() => {
                        const [month, day, year] =
                          formState.chequeDate.split("/");
                        return `${year}-${month.padStart(
                          2,
                          "0",
                        )}-${day.padStart(2, "0")}`;
                      })()
                    : formState.chequeDate
                }
                onChange={(e) => {
                  const selectedDate = new Date(e.target.value + "T00:00:00");
                  const formattedDate = `${(selectedDate.getMonth() + 1)
                    .toString()
                    .padStart(2, "0")}/${selectedDate
                    .getDate()
                    .toString()
                    .padStart(2, "0")}/${selectedDate.getFullYear()}`;
                  setFormState((prev) => ({
                    ...prev,
                    chequeDate: formattedDate,
                  }));
                }}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
              />
            ) : (
              <input
                type="text"
                name="chequeDate"
                value={formState.chequeDate}
                readOnly
                className="mt-1 block w-full rounded-md border-gray-300 bg-gray-100 shadow-sm"
              />
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Bank Account
            </label>
            <input
              type="text"
              name="bankAccount"
              value={formState.bankAccount}
              readOnly
              className="mt-1 block w-full rounded-md border-gray-300 bg-gray-100 shadow-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Agent Number
            </label>
            <input
              type="text"
              name="agentNumber"
              value={formState.agentNumber}
              readOnly
              className="mt-1 block w-full rounded-md border-gray-300 bg-gray-100 shadow-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Agent Name
            </label>
            <input
              type="text"
              name="agentName"
              value={formState.agentName}
              readOnly
              className="mt-1 block w-full rounded-md border-gray-300 bg-gray-100 shadow-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Trade #
            </label>
            <input
              type="text"
              name="tradeNumber"
              value={formState.tradeNumber}
              readOnly
              className="mt-1 block w-full rounded-md border-gray-300 bg-gray-100 shadow-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Address
            </label>
            <input
              type="text"
              name="address"
              value={formState.address}
              readOnly
              className="mt-1 block w-full rounded-md border-gray-300 bg-gray-100 shadow-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Net Commission
            </label>
            <input
              type="text"
              name="netCommission"
              value={formState.netCommission}
              readOnly
              className="mt-1 block w-full rounded-md border-gray-300 bg-gray-100 shadow-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Net Cheque
            </label>
            <input
              type="text"
              name="netCheque"
              value={formState.netCheque}
              readOnly
              className="mt-1 block w-full rounded-md border-gray-300 bg-gray-100 shadow-sm"
            />
          </div>
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700">
            Note on Stub
          </label>
          <input
            type="text"
            name="noteOnStub"
            value={formState.noteOnStub}
            readOnly
            className="mt-1 block w-full rounded-md border-gray-300 bg-gray-100 shadow-sm"
          />
        </div>

        <div className="flex justify-start space-x-4 mt-6">
          <button className="bg-gray-200 text-black py-2 px-6 rounded-md hover:bg-gray-300 transition-colors">
            Print
          </button>
          <button className="bg-gray-200 text-black py-2 px-6 rounded-md hover:bg-gray-300 transition-colors">
            Batch
          </button>
          <button
            onClick={() => handleAgentCommissionEFTClick(formState)}
            disabled={eftCreatedForAgent}
            className={`py-2 px-6 rounded-md transition-colors ${
              eftCreatedForAgent
                ? "bg-gray-400 text-gray-600 cursor-not-allowed"
                : "bg-gray-200 text-black hover:bg-gray-300"
            }`}
          >
            {eftCreatedForAgent ? "EFT Created" : "E.F.T."}
          </button>
          <button
            onClick={onBack}
            className="bg-red-500 text-white py-2 px-6 rounded-md hover:bg-red-600 transition-colors"
          >
            Exit
          </button>
        </div>
      </div>
    );
  };

  const OutsideBrokersCommissionDetailsForm = ({
    trade,
    selectedOutsideBroker,
    onBack,
    eftCreated,
    onEftCreated,
  }) => {
    const [formState, setFormState] = useState({
      chequeDate: getCurrentMDYDate(),
      bankAccount: "Commission Trust",
      outsideBrokerName: "",
      outsideBrokerCompany: "",
      outsideBrokerAddress: "",
      outsideBrokerStreetNumber: "",
      outsideBrokerStreetName: "",
      outsideBrokerUnit: "",
      outsideBrokerProvince: "",
      outsideBrokerPostalCode: "",
      tradeNumber: "",
      address: "",
      netCommission: "",
      netCheque: "",
      noteOnStub: "Transfer commission to outside broker",
      seller: "",
      buyer: "",
    });

    const [useCustomDate, setUseCustomDate] = useState(false);

    // Build full address from outside broker's address fields (from Outside Brokers section)
    const buildBrokerAddress = (broker) => {
      if (!broker) return "";
      const street = [broker.streetNumber, broker.streetName]
        .filter(Boolean)
        .map((s) => String(s).trim())
        .join(" ");
      const parts = [street];
      if (broker.unitNumber && String(broker.unitNumber).trim()) {
        parts.push(`Unit ${String(broker.unitNumber).trim()}`);
      }
      if (broker.province && String(broker.province).trim()) {
        parts.push(String(broker.province).trim());
      }
      if (broker.postalCode && String(broker.postalCode).trim()) {
        parts.push(String(broker.postalCode).trim());
      }
      const built = parts.filter(Boolean).join(", ");
      return built || (broker.address && String(broker.address).trim()) || "";
    };

    useEffect(() => {
      if (trade && selectedOutsideBroker) {
        let netCommission = 0;
        const referralAgent = (trade.agentCommissionList || []).find(
          (a) =>
            (a.classification || "").toUpperCase() === "REFERRAL" &&
            (a.agentName === selectedOutsideBroker.agentName ||
              a.agentId === selectedOutsideBroker.agentId),
        );
        if (selectedOutsideBroker._isReferral) {
          // Referral: use net commission (Total - Fees) for EFT, same as Agent Info "Net" column
          netCommission = parseFloat(
            referralAgent?.netCommission ||
              referralAgent?.total ||
              referralAgent?.amount ||
              0,
          );
        } else {
          // Outside broker: get commission from outsideBrokersRows
          const outsideBrokerCommission =
            trade.commission?.outsideBrokersRows?.find(
              (broker) =>
                broker.agentName === selectedOutsideBroker.agentName ||
                `${selectedOutsideBroker.firstName} ${selectedOutsideBroker.lastName}` ===
                  broker.agentName,
            ) || {};
          netCommission = parseFloat(outsideBrokerCommission.total || 0);
        }

        const address = `${trade.keyInfo?.streetNumber || ""} ${
          trade.keyInfo?.streetName || ""
        }${trade.keyInfo?.unit ? ` ${trade.keyInfo.unit}` : ""}`.trim();

        // Get party names based on deal type
        const partyNames = getPartyNamesByDealType(trade);
        const sellers = partyNames.firstParty;
        const buyers = partyNames.secondParty;

        // For referral agents: use company address details from the agent (same as Outside Brokers section) for EFT
        let brokerForAddress = selectedOutsideBroker;
        if (selectedOutsideBroker._isReferral && referralAgent) {
          brokerForAddress = {
            ...selectedOutsideBroker,
            streetNumber:
              referralAgent.streetNumber ?? selectedOutsideBroker.streetNumber,
            streetName:
              referralAgent.streetName ?? selectedOutsideBroker.streetName,
            unitNumber:
              referralAgent.unitNumber ?? selectedOutsideBroker.unitNumber,
            province: referralAgent.province ?? selectedOutsideBroker.province,
            postalCode:
              referralAgent.postalCode ?? selectedOutsideBroker.postalCode,
            address: referralAgent.address ?? selectedOutsideBroker.address,
          };
        }
        const outsideBrokerAddress = buildBrokerAddress(brokerForAddress);

        // For referral agents, show agent name as company (used on form and when creating EFT)
        const brokerName =
          selectedOutsideBroker.agentName ||
          `${selectedOutsideBroker.firstName} ${selectedOutsideBroker.lastName}`.trim();
        const companyForForm =
          selectedOutsideBroker._isReferral ||
          selectedOutsideBroker.company === "Referral"
            ? brokerName
            : selectedOutsideBroker.company || "";

        setFormState({
          chequeDate: getCurrentMDYDate(),
          bankAccount: "Commission Trust",
          outsideBrokerName: brokerName,
          outsideBrokerCompany: companyForForm,
          outsideBrokerAddress,
          outsideBrokerStreetNumber: brokerForAddress.streetNumber || "",
          outsideBrokerStreetName: brokerForAddress.streetName || "",
          outsideBrokerUnit: brokerForAddress.unitNumber || "",
          outsideBrokerProvince: brokerForAddress.province || "",
          outsideBrokerPostalCode: brokerForAddress.postalCode || "",
          tradeNumber: trade.tradeNumber,
          address: address,
          netCommission: netCommission.toFixed(2),
          netCheque: netCommission.toFixed(2),
          noteOnStub: "Transfer commission to outside broker",
          seller: sellers,
          buyer: buyers,
        });
      }
    }, [trade, selectedOutsideBroker]);

    const handleInputChange = (e) => {
      const { name, value } = e.target;
      setFormState((prev) => ({
        ...prev,
        [name]: value,
      }));
    };

    const handleEFTClick = async () => {
      const tradeAddress = `${trade.keyInfo?.streetNumber || ""} ${
        trade.keyInfo?.streetName || ""
      }${trade.keyInfo?.unit ? ` ${trade.keyInfo.unit}` : ""}`.trim();

      // Get party names based on deal type
      const partyNames = getPartyNamesByDealType(trade);
      const sellers = partyNames.firstParty;
      const buyers = partyNames.secondParty;

      try {
        console.log("Creating EFT for outside broker commission...");
        console.log("Trade ID:", trade._id);
        console.log("Amount:", formState.netCommission);
        console.log("Recipient:", formState.outsideBrokerName);

        // 1. Fetch Company Profile (optional - use default if not available)
        let companyInfo = null;
        try {
          const companyProfileRes = await axiosInstance.get("/company-profile");
          companyInfo = companyProfileRes.data;
        } catch (companyError) {
          if (companyError.response?.status === 404) {
            console.log("Company profile not found, using default values");
            // Use default company info if profile doesn't exist
            companyInfo = {
              companyName: "ABC Realty Inc., Brokerage",
              address: "634 Champlain St",
              city: "Dieppe",
              province: "NB",
              postalCode: "E1A 1P4",
              phone: "123 456 7890",
              fax: "123 456 7891",
              email: "email@company.com",
            };
          } else {
            throw companyError;
          }
        }

        // For referral agents, use agent name as company name on the EFT (not "Referral")
        const isReferralAgent = formState.outsideBrokerCompany === "Referral";
        const eftCompanyName = isReferralAgent
          ? formState.outsideBrokerName
          : formState.outsideBrokerCompany;
        const eftRecipient = eftCompanyName
          ? `${formState.outsideBrokerName} - ${eftCompanyName}`
          : formState.outsideBrokerName;

        // 2. Create EFT record
        const eftResponse = await axiosInstance.post(
          "/commission-trust-eft/outside-broker",
          {
            tradeId: trade._id,
            amount: Number(formState.netCommission),
            recipient: eftRecipient,
            description: "Transfer commission to outside broker",
            chequeDate: formState.chequeDate,
          },
        );

        console.log("EFT Response:", eftResponse.data);
        const eftNumber = eftResponse.data.eftNumber;

        setOutsideBrokersReceiptData({
          companyInfo: companyInfo,
          eftNumber: eftNumber,
          chequeDate: formState.chequeDate,
          paidTo: eftCompanyName || formState.outsideBrokerName,
          trade: `${trade.tradeNumber} ${tradeAddress}`,
          notes: "Transfer commission to outside broker",
          amount: formState.netCommission,
          seller: sellers,
          buyer: buyers,
          firstPartyLabel: partyNames.firstPartyLabel,
          secondPartyLabel: partyNames.secondPartyLabel,
          payTo: eftCompanyName || formState.outsideBrokerName,
          orderOf: {
            address: formState.outsideBrokerAddress,
            streetNumber: formState.outsideBrokerStreetNumber,
            streetName: formState.outsideBrokerStreetName,
            unitNumber: formState.outsideBrokerUnit,
            province: formState.outsideBrokerProvince,
            postalCode: formState.outsideBrokerPostalCode,
            cityProvincePostal: [
              formState.outsideBrokerProvince,
              formState.outsideBrokerPostalCode,
            ]
              .filter(Boolean)
              .join(", "),
          },
          note: `${trade.tradeNumber} ${tradeAddress}`,
        });
        setShowOutsideBrokersReceipt(true);
        // Mark EFT as created for this broker only
        onEftCreated?.();
        toast.success(
          "Transaction posted to Chart of Accounts and Ledger successfully!",
        );
      } catch (error) {
        console.error("Error creating EFT record:", error);
        console.error("Error response:", error.response?.data);
        console.error("Error status:", error.response?.status);
        console.error("Error message:", error.message);
        alert("Error creating EFT record. Please try again.");
      }
    };

    return (
      <div className="p-6 border border-gray-200 rounded-lg">
        <h3 className="font-semibold mb-4 text-lg">
          Outside Brokers Commission Cheque Details
        </h3>

        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Cheque Date
            </label>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="useCustomDateOutside"
                checked={useCustomDate}
                onChange={(e) => setUseCustomDate(e.target.checked)}
                className="rounded border-gray-300"
              />
              <label
                htmlFor="useCustomDateOutside"
                className="text-sm text-gray-600"
              >
                Override Date
              </label>
            </div>
            {useCustomDate ? (
              <input
                type="date"
                name="chequeDate"
                value={
                  formState.chequeDate.includes("/")
                    ? (() => {
                        const [month, day, year] =
                          formState.chequeDate.split("/");
                        return `${year}-${month.padStart(
                          2,
                          "0",
                        )}-${day.padStart(2, "0")}`;
                      })()
                    : formState.chequeDate
                }
                onChange={(e) => {
                  const selectedDate = new Date(e.target.value + "T00:00:00");
                  const formattedDate = `${(selectedDate.getMonth() + 1)
                    .toString()
                    .padStart(2, "0")}/${selectedDate
                    .getDate()
                    .toString()
                    .padStart(2, "0")}/${selectedDate.getFullYear()}`;
                  setFormState((prev) => ({
                    ...prev,
                    chequeDate: formattedDate,
                  }));
                }}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
              />
            ) : (
              <input
                type="text"
                name="chequeDate"
                value={formState.chequeDate}
                readOnly
                className="mt-1 block w-full rounded-md border-gray-300 bg-gray-100 shadow-sm"
              />
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Bank Account
            </label>
            <input
              type="text"
              name="bankAccount"
              value={formState.bankAccount}
              readOnly
              className="mt-1 block w-full rounded-md border-gray-300 bg-gray-100 shadow-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Outside Broker Name
            </label>
            <input
              type="text"
              name="outsideBrokerName"
              value={formState.outsideBrokerName}
              readOnly
              className="mt-1 block w-full rounded-md border-gray-300 bg-gray-100 shadow-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Outside Broker Company
            </label>
            <input
              type="text"
              name="outsideBrokerCompany"
              value={formState.outsideBrokerCompany}
              readOnly
              className="mt-1 block w-full rounded-md border-gray-300 bg-gray-100 shadow-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Street #
            </label>
            <input
              type="text"
              name="outsideBrokerStreetNumber"
              value={formState.outsideBrokerStreetNumber}
              readOnly
              className="mt-1 block w-full rounded-md border-gray-300 bg-gray-100 shadow-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Street Name
            </label>
            <input
              type="text"
              name="outsideBrokerStreetName"
              value={formState.outsideBrokerStreetName}
              readOnly
              className="mt-1 block w-full rounded-md border-gray-300 bg-gray-100 shadow-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Unit #
            </label>
            <input
              type="text"
              name="outsideBrokerUnit"
              value={formState.outsideBrokerUnit}
              readOnly
              className="mt-1 block w-full rounded-md border-gray-300 bg-gray-100 shadow-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Province
            </label>
            <input
              type="text"
              name="outsideBrokerProvince"
              value={formState.outsideBrokerProvince}
              readOnly
              className="mt-1 block w-full rounded-md border-gray-300 bg-gray-100 shadow-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Postal Code
            </label>
            <input
              type="text"
              name="outsideBrokerPostalCode"
              value={formState.outsideBrokerPostalCode}
              readOnly
              className="mt-1 block w-full rounded-md border-gray-300 bg-gray-100 shadow-sm"
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700">
              Outside Broker Address
            </label>
            <input
              type="text"
              name="outsideBrokerAddress"
              value={formState.outsideBrokerAddress}
              readOnly
              className="mt-1 block w-full rounded-md border-gray-300 bg-gray-100 shadow-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Trade Number
            </label>
            <input
              type="text"
              name="tradeNumber"
              value={formState.tradeNumber}
              readOnly
              className="mt-1 block w-full rounded-md border-gray-300 bg-gray-100 shadow-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Address
            </label>
            <input
              type="text"
              name="address"
              value={formState.address}
              readOnly
              className="mt-1 block w-full rounded-md border-gray-300 bg-gray-100 shadow-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Net Commission
            </label>
            <input
              type="text"
              name="netCommission"
              value={formState.netCommission}
              readOnly
              className="mt-1 block w-full rounded-md border-gray-300 bg-gray-100 shadow-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Net Cheque
            </label>
            <input
              type="text"
              name="netCheque"
              value={formState.netCheque}
              readOnly
              className="mt-1 block w-full rounded-md border-gray-300 bg-gray-100 shadow-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Seller
            </label>
            <input
              type="text"
              name="seller"
              value={formState.seller}
              readOnly
              className="mt-1 block w-full rounded-md border-gray-300 bg-gray-100 shadow-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Buyer
            </label>
            <input
              type="text"
              name="buyer"
              value={formState.buyer}
              readOnly
              className="mt-1 block w-full rounded-md border-gray-300 bg-gray-100 shadow-sm"
            />
          </div>
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700">
            Note on Stub
          </label>
          <input
            type="text"
            name="noteOnStub"
            value={formState.noteOnStub}
            readOnly
            className="mt-1 block w-full rounded-md border-gray-300 bg-gray-100 shadow-sm"
          />
        </div>

        <div className="flex justify-start space-x-4 mt-6">
          <button className="bg-gray-200 text-black py-2 px-6 rounded-md hover:bg-gray-300 transition-colors">
            Print
          </button>
          <button className="bg-gray-200 text-black py-2 px-6 rounded-md hover:bg-gray-300 transition-colors">
            Batch
          </button>
          <button
            onClick={handleEFTClick}
            disabled={eftCreated}
            className={`py-2 px-6 rounded-md transition-colors ${
              eftCreated
                ? "bg-gray-400 text-gray-600 cursor-not-allowed"
                : "bg-gray-200 text-black hover:bg-gray-300"
            }`}
          >
            {eftCreated ? "EFT Created" : "E.F.T."}
          </button>
          <button
            onClick={onBack}
            className="bg-red-500 text-white py-2 px-6 rounded-md hover:bg-red-600 transition-colors"
          >
            Exit
          </button>
        </div>
      </div>
    );
  };

  // Our Brokerage Commission Details Form
  const OurBrokerageCommissionDetailsForm = ({ trade, onBack, eftCreated }) => {
    const [formState, setFormState] = useState({
      chequeDate: getCurrentMDYDate(),
      bankAccount: "Commission Trust",
      tradeNumber: trade?.tradeNumber || "",
      noteOnStub: "Transfer Office Share",
    });

    const [useCustomDate, setUseCustomDate] = useState(false);

    // Live amount from sheet Net to Office Total (+ rebates). Read-only — do not cache in
    // form state or a hot reload / stale trade can leave pre-HST sub-total (e.g. 1000 vs 1130).
    const officeShareAmount = trade ? getOurBrokerageEftAmount(trade) : "";
    const {
      netToOfficeSubTotal = "0.00",
      netToOfficeHST = "0.00",
      netToOfficeTotal = "0.00",
    } = trade ? getNetToOfficeTotal(trade) : {};
    const totalBuyerRebates = trade ? getTotalBuyerRebateAmount(trade) : 0;

    useEffect(() => {
      if (trade) {
        setFormState((prev) => ({
          ...prev,
          tradeNumber: trade.tradeNumber,
          noteOnStub:
            getTotalBuyerRebateAmount(trade) > 0
              ? "Transfer Office Share (includes buyer rebates)"
              : "Transfer Office Share",
        }));
      }
    }, [trade]);
    // --- E.F.T. button logic ---
    const handleEFTClick = async () => {
      if (!trade) return;
      const tradeAddress = `${trade.keyInfo?.streetNumber || ""} ${
        trade.keyInfo?.streetName || ""
      }${trade.keyInfo?.unit ? ` ${trade.keyInfo.unit}` : ""}`.trim();
      const amount = officeShareAmount;
      try {
        // 1. Create EFT record (endpoint must exist in backend)
        const eftResponse = await axiosInstance.post(
          "/commission-trust-eft/our-brokerage",
          {
            tradeId: trade._id,
            amount: Number(amount),
            recipient: "ABC Realty Inc., Brokerage ",
            description: "Transfer Office Share",
            chequeDate: formState.chequeDate,
          },
        );
        const eftNumber = eftResponse.data.eftNumber;
        // 2. Post transaction - Credit 10004, Debit 10001
        await axiosInstance.post("/transactions", {
          date: (() => {
            const [month, day, year] = formState.chequeDate.split("/");
            return new Date(
              `${year}-${month.padStart(2, "0")}-${day.padStart(
                2,
                "0",
              )}T00:00:00`,
            );
          })(),
          reference: `EFT#${eftNumber}`,
          description: "Our Brokerage Commission Transfer",
          debitAccount: "CASH - CURRENT ACCOUNT", // 10001
          creditAccount: "CASH - COMMISSION TRUST ACCOUNT", // 10004
          amount: Number(amount),
          tradeId: trade._id,
        });
        const totalBuyerRebates = getTotalBuyerRebateAmount(trade);

        // 3. Prepare receipt data
        const dataForReceipt = {
          eftNumber: eftNumber,
          paidTo: "ABC Realty Inc., Brokerage ",
          trade: `${trade.tradeNumber} ${tradeAddress}`,
          notes:
            totalBuyerRebates > 0
              ? "Transfer Office Share (includes buyer rebates)"
              : "Transfer Office Share",
          amount: amount,
          payTo: "ABC Realty Inc., Brokerage",
          orderOf: {
            address: "634 Champlain St",
            cityProvincePostal: "Dieppe, NB E1A 1P4",
          },
          note: `${trade.tradeNumber} ${tradeAddress}`,
          chequeDate: formState.chequeDate,
        };
        setEftReceiptData(dataForReceipt);
        setIsOurBrokerageEFT(true);
        setShowEFTReceipt(true);
        // Set EFT as created to disable the button
        setEftCreated((prev) => ({ ...prev, ourBrokerageCommission: true }));
        toast.success(
          "Transaction posted to Chart of Accounts and Ledger successfully!",
        );
      } catch (error) {
        console.error("Error during EFT process:", error);
        toast.error(
          "Failed to complete EFT process. Please try again. (Check if backend endpoint exists)",
        );
      }
    };
    // --- end E.F.T. logic ---
    return (
      <div className="p-6 border border-gray-200 rounded-lg">
        <h3 className="font-semibold mb-4 text-lg">
          Our Brokerage Commission Cheque Details
        </h3>
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Cheque Date
            </label>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="useCustomDateOurBrokerage"
                checked={useCustomDate}
                onChange={(e) => setUseCustomDate(e.target.checked)}
                className="rounded border-gray-300"
              />
              <label
                htmlFor="useCustomDateOurBrokerage"
                className="text-sm text-gray-600"
              >
                Override Date
              </label>
            </div>
            {useCustomDate ? (
              <input
                type="date"
                name="chequeDate"
                value={
                  formState.chequeDate.includes("/")
                    ? (() => {
                        const [month, day, year] =
                          formState.chequeDate.split("/");
                        return `${year}-${month.padStart(
                          2,
                          "0",
                        )}-${day.padStart(2, "0")}`;
                      })()
                    : formState.chequeDate
                }
                onChange={(e) => {
                  const selectedDate = new Date(e.target.value + "T00:00:00");
                  const formattedDate = `${(selectedDate.getMonth() + 1)
                    .toString()
                    .padStart(2, "0")}/${selectedDate
                    .getDate()
                    .toString()
                    .padStart(2, "0")}/${selectedDate.getFullYear()}`;
                  setFormState((prev) => ({
                    ...prev,
                    chequeDate: formattedDate,
                  }));
                }}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
              />
            ) : (
              <input
                type="text"
                name="chequeDate"
                value={formState.chequeDate}
                readOnly
                className="mt-1 block w-full rounded-md border-gray-300 bg-gray-100 shadow-sm"
              />
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Bank Account
            </label>
            <input
              type="text"
              name="bankAccount"
              value={formState.bankAccount}
              readOnly
              className="mt-1 block w-full rounded-md border-gray-300 bg-gray-100 shadow-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Trade #
            </label>
            <input
              type="text"
              name="tradeNumber"
              value={formState.tradeNumber}
              readOnly
              className="mt-1 block w-full rounded-md border-gray-300 bg-gray-100 shadow-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Amount
            </label>
            {trade && (
              <p className="text-xs text-gray-600 mb-1">
                Tax rate applied:{" "}
                <strong>
                  {`${Math.round(getTradeTaxRate(trade) * 100)}%`}
                </strong>
                {" · "}
                Net to Office Total (sub-total + HST)
                {totalBuyerRebates > 0 ? " + buyer rebates" : ""}
              </p>
            )}
            <input
              type="text"
              name="amount"
              value={officeShareAmount}
              readOnly
              className="mt-1 block w-full rounded-md border-gray-300 bg-gray-100 shadow-sm"
            />
          </div>
          {trade && (
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Net to Office (same as Trade Details sheet)
              </label>
              <div className="bg-gray-50 p-3 rounded-md text-sm">
                <div className="flex justify-between mb-1">
                  <span>Sub-total:</span>
                  <span className="tabular-nums">${netToOfficeSubTotal}</span>
                </div>
                <div className="flex justify-between mb-1">
                  <span>HST:</span>
                  <span className="tabular-nums">${netToOfficeHST}</span>
                </div>
                <div className="flex justify-between mb-1 font-medium">
                  <span>Net to Office Total:</span>
                  <span className="tabular-nums">${netToOfficeTotal}</span>
                </div>
                {totalBuyerRebates > 0 && (
                  <div className="flex justify-between mb-1 text-gray-600">
                    <span>
                      Buyer rebates (added for EFT; Liabilities on sheet):
                    </span>
                    <span className="tabular-nums">
                      ${totalBuyerRebates.toFixed(2)}
                    </span>
                  </div>
                )}
                <div className="flex justify-between font-semibold border-t pt-1">
                  <span>
                    {totalBuyerRebates > 0
                      ? "EFT Amount (Net to Office + rebates):"
                      : "EFT Amount:"}
                  </span>
                  <span className="tabular-nums">${officeShareAmount}</span>
                </div>
              </div>
            </div>
          )}
          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-700">
              Note on Stub
            </label>
            <input
              type="text"
              name="noteOnStub"
              value={formState.noteOnStub}
              readOnly
              className="mt-1 block w-full rounded-md border-gray-300 bg-gray-100 shadow-sm"
            />
          </div>
        </div>
        <div className="flex justify-start space-x-4 mt-6">
          <button className="bg-gray-200 text-black py-2 px-6 rounded-md hover:bg-gray-300 transition-colors">
            Print
          </button>
          <button
            onClick={handleEFTClick}
            disabled={eftCreated}
            className={`py-2 px-6 rounded-md transition-colors ${
              eftCreated
                ? "bg-gray-400 text-gray-600 cursor-not-allowed"
                : "bg-gray-200 text-black hover:bg-gray-300"
            }`}
          >
            {eftCreated ? "EFT Created" : "E.F.T."}
          </button>
          <button
            onClick={onBack}
            className="bg-red-500 text-white py-2 px-6 rounded-md hover:bg-red-600 transition-colors"
          >
            Exit
          </button>
        </div>
      </div>
    );
  };

  // Add state and handlers for Our Brokerage Commission
  const handleOurBrokerageTradeSearch = (searchTerm) => {
    const q = String(searchTerm ?? "");
    setOurBrokerageForm((prev) => ({
      ...prev,
      tradeNumber: q,
      selectedTrade: null,
    }));
    if (q.trim() === "") {
      setFilteredTrades([]);
      setShowTradeDropdown(false);
      return;
    }
    setFilteredTrades(filterTradesBySearch(q));
    setShowTradeDropdown(true);
  };
  const handleBuilderRepaymentTradeSearch = (searchTerm) => {
    const q = String(searchTerm ?? "");
    setBuilderRepaymentForm((prev) => ({
      ...prev,
      tradeNumber: q,
      selectedTrade: null,
      builderName: "",
      suggestedRepaymentAmount: null,
    }));
    if (q.trim() === "") {
      setFilteredTrades([]);
      setShowTradeDropdown(false);
      return;
    }
    setFilteredTrades(filterTradesBySearch(q));
    setShowTradeDropdown(true);
  };

  const selectBuilderRepaymentTrade = async (trade) => {
    const address = `${trade.keyInfo?.streetNumber || ""} ${
      trade.keyInfo?.streetName || ""
    }${trade.keyInfo?.unit ? ` ${trade.keyInfo.unit}` : ""}`.trim();
    try {
      const existingEFT = await checkCommissionTrustEFT(
        trade._id,
        "BuilderRepayment",
      );
      if (existingEFT.exists) {
        toast.error(
          `Builder repayment already completed for Trade #${trade.tradeNumber}!\nEFT #${existingEFT.eftNumber} was created on ${formatDate(existingEFT.date)}.`,
          { duration: 6000 },
        );
        setBuilderRepaymentEftCreated(true);
        setShowTradeDropdown(false);
        return;
      }

      const [tradeRes, builderRes] = await Promise.all([
        axiosInstance.get(`/trades/${trade._id}`),
        axiosInstance.get(`/commission-trust-eft/builder-name/${trade._id}`),
      ]);
      const fullTrade = tradeRes.data;
      const builderName = builderRes.data?.builderName || "";
      const suggestedRepaymentAmount =
        builderRes.data?.suggestedRepaymentAmount ?? null;
      if (!builderName) {
        toast.error(
          builderRes.data?.message ||
            "Builder name not found. Record Received From when finalizing the trade first.",
          { duration: 6000 },
        );
      }
      setBuilderRepaymentForm((prev) => ({
        ...prev,
        tradeNumber: `${trade.tradeNumber} - ${address}`,
        selectedTrade: fullTrade,
        builderName,
        suggestedRepaymentAmount,
      }));
      setBuilderRepaymentEftCreated(false);
      setShowTradeDropdown(false);
    } catch (error) {
      console.error("Error loading builder repayment trade:", error);
      toast.error("Could not load trade details. Please try again.");
    }
  };

  const selectOurBrokerageTrade = async (trade) => {
    const address = `${trade.keyInfo?.streetNumber || ""} ${
      trade.keyInfo?.streetName || ""
    }${trade.keyInfo?.unit ? ` ${trade.keyInfo.unit}` : ""}`.trim();
    try {
      // Check if Our Brokerage Commission EFT already exists for this trade
      const existingEFT = await checkCommissionTrustEFT(
        trade._id,
        "OurBrokerageCommission",
      );
      if (existingEFT.exists) {
        toast.error(
          `Payment already completed for Trade #${trade.tradeNumber}!\nEFT #${
            existingEFT.eftNumber
          } was created on ${formatDate(
            existingEFT.date,
          )} for ${getEFTTypeDisplayName(existingEFT.type)}.`,
          { duration: 6000 },
        );
        // Set EFT as created to disable the button
        setEftCreated((prev) => ({ ...prev, ourBrokerageCommission: true }));
        return;
      }
      const response = await axiosInstance.get(`/trades/${trade._id}`);
      const fullTrade = response.data;
      setOurBrokerageForm((prev) => ({
        ...prev,
        tradeNumber: `${trade.tradeNumber} - ${address}`,
        selectedTrade: fullTrade,
      }));
      setShowTradeDropdown(false);
    } catch (error) {
      console.error("Error fetching full trade details:", error);
      toast.error("Could not load trade details. Please try again.");
    }
  };

  const handleGeneralToCommissionTradeSearch = (searchTerm) => {
    const q = String(searchTerm ?? "");
    setGeneralToCommissionForm((prev) => ({
      ...prev,
      tradeNumber: q,
      selectedTrade: null,
    }));
    if (q.trim() === "") {
      setFilteredTrades([]);
      setShowTradeDropdown(false);
      return;
    }
    setFilteredTrades(filterTradesBySearch(q));
    setShowTradeDropdown(true);
  };

  const selectGeneralToCommissionTrade = async (trade) => {
    const address = `${trade.keyInfo?.streetNumber || ""} ${
      trade.keyInfo?.streetName || ""
    }${trade.keyInfo?.unit ? ` ${trade.keyInfo.unit}` : ""}`.trim();
    try {
      const existingEFT = await checkCommissionTrustEFT(
        trade._id,
        "GeneralToCommissionTransfer",
      );
      if (existingEFT.exists) {
        toast.error(
          `General to Commission transfer already completed for Trade #${trade.tradeNumber}!\nEFT #${existingEFT.eftNumber} was created on ${formatDate(existingEFT.date)}.`,
          { duration: 6000 },
        );
        setGeneralToCommissionEftCreated(true);
        setShowTradeDropdown(false);
        return;
      }

      const response = await axiosInstance.get(`/trades/${trade._id}`);
      setGeneralToCommissionForm({
        tradeNumber: `${trade.tradeNumber} - ${address}`,
        selectedTrade: response.data,
      });
      setGeneralToCommissionEftCreated(false);
      setShowTradeDropdown(false);
    } catch (error) {
      console.error("Error loading trade for general to commission:", error);
      toast.error("Could not load trade details. Please try again.");
    }
  };

  const GeneralToCommissionDetailsForm = ({ trade, onBack, eftCreated }) => {
    const suggestedTransferAmount = trade
      ? getNetToOfficeTotal(trade).netToOfficeTotal || ""
      : "";

    const [formState, setFormState] = useState({
      chequeDate: getCurrentMDYDate(),
      bankAccount: "Commission Trust",
      tradeNumber: trade?.tradeNumber || "",
      amount: suggestedTransferAmount,
      noteOnStub: "Commission transfer from General to Commission",
    });
    const [useCustomDate, setUseCustomDate] = useState(false);

    useEffect(() => {
      if (trade) {
        const { netToOfficeTotal } = getNetToOfficeTotal(trade);
        setFormState((prev) => ({
          ...prev,
          tradeNumber: trade.tradeNumber,
          amount:
            netToOfficeTotal && parseFloat(netToOfficeTotal) > 0
              ? netToOfficeTotal
              : prev.amount,
        }));
      }
    }, [trade]);

    const handleInputChange = (e) => {
      const { name, value } = e.target;
      setFormState((prev) => ({ ...prev, [name]: value }));
    };

    const handleEFTClick = async () => {
      if (!trade) return;
      const amount = Number(formState.amount);
      if (!Number.isFinite(amount) || amount <= 0) {
        toast.error("Please enter a valid transfer amount.");
        return;
      }

      const existingEFT = await checkCommissionTrustEFT(
        trade._id,
        "GeneralToCommissionTransfer",
      );
      if (existingEFT.exists) {
        toast.error(
          `General to Commission transfer already completed for Trade #${trade.tradeNumber}!\nEFT #${existingEFT.eftNumber} was created on ${formatDate(existingEFT.date)}.`,
          { duration: 6000 },
        );
        setGeneralToCommissionEftCreated(true);
        return;
      }

      const tradeAddress = `${trade.keyInfo?.streetNumber || ""} ${
        trade.keyInfo?.streetName || ""
      }${trade.keyInfo?.unit ? ` ${trade.keyInfo.unit}` : ""}`.trim();
      const partyNames = getPartyNamesByDealType(trade);

      try {
        const eftResponse = await axiosInstance.post(
          "/commission-trust-eft/general-to-commission",
          {
            tradeId: trade._id,
            amount,
            chequeDate: formState.chequeDate,
            notes: formState.noteOnStub,
          },
        );
        const eftNum = eftResponse.data.eftNumber;

        setEftReceiptData({
          eftNumber: eftNum,
          paidTo: "ABC Realty Inc., Brokerage",
          trade: `${trade.tradeNumber} ${tradeAddress}`,
          notes:
            formState.noteOnStub ||
            "Commission transfer from General to Commission",
          amount: String(amount),
          payTo: "ABC Realty Inc., Brokerage",
          orderOf: {
            address: "634 Champlain St",
            cityProvincePostal: "Dieppe, NB E1A 1P4",
          },
          note: `${trade.tradeNumber} ${tradeAddress}`,
          chequeDate: formState.chequeDate,
          seller: partyNames.firstParty,
          buyer: partyNames.secondParty,
          firstPartyLabel: partyNames.firstPartyLabel,
          secondPartyLabel: partyNames.secondPartyLabel,
        });
        setIsOurBrokerageEFT(true);
        setShowEFTReceipt(true);
        setGeneralToCommissionEftCreated(true);
        toast.success(
          "Transfer posted to ledger and EFT created successfully!",
        );
      } catch (error) {
        console.error("Error creating general to commission transfer:", error);
        toast.error(
          error.response?.data?.message ||
            "Failed to create transfer. Please try again.",
        );
      }
    };

    return (
      <div className="p-6 border border-gray-200 rounded-lg">
        <h3 className="font-semibold mb-4 text-lg">
          General to Commission Transfer Details
        </h3>
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Cheque Date
            </label>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="useCustomDateGenComm"
                checked={useCustomDate}
                onChange={(e) => setUseCustomDate(e.target.checked)}
                className="rounded border-gray-300"
              />
              <label
                htmlFor="useCustomDateGenComm"
                className="text-sm text-gray-600"
              >
                Override Date
              </label>
            </div>
            {useCustomDate ? (
              <input
                type="date"
                name="chequeDate"
                value={
                  formState.chequeDate.includes("/")
                    ? (() => {
                        const [month, day, year] =
                          formState.chequeDate.split("/");
                        return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
                      })()
                    : formState.chequeDate
                }
                onChange={(e) => {
                  const selectedDate = new Date(e.target.value + "T00:00:00");
                  const formattedDate = `${(selectedDate.getMonth() + 1)
                    .toString()
                    .padStart(2, "0")}/${selectedDate
                    .getDate()
                    .toString()
                    .padStart(2, "0")}/${selectedDate.getFullYear()}`;
                  setFormState((prev) => ({
                    ...prev,
                    chequeDate: formattedDate,
                  }));
                }}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
              />
            ) : (
              <input
                type="text"
                name="chequeDate"
                value={formState.chequeDate}
                readOnly
                className="mt-1 block w-full rounded-md border-gray-300 bg-gray-100 shadow-sm"
              />
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Bank Account
            </label>
            <input
              type="text"
              name="bankAccount"
              value={formState.bankAccount}
              readOnly
              className="mt-1 block w-full rounded-md border-gray-300 bg-gray-100 shadow-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Trade #
            </label>
            <input
              type="text"
              name="tradeNumber"
              value={formState.tradeNumber}
              readOnly
              className="mt-1 block w-full rounded-md border-gray-300 bg-gray-100 shadow-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Transfer Amount
            </label>
            <input
              type="number"
              name="amount"
              value={formState.amount}
              onChange={handleInputChange}
              min="0.01"
              step="0.01"
              placeholder="Enter amount"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
            />
          </div>
          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-700">
              Note on Stub
            </label>
            <input
              type="text"
              name="noteOnStub"
              value={formState.noteOnStub}
              onChange={handleInputChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
            />
          </div>
        </div>
        <div className="flex space-x-4">
          <button
            type="button"
            onClick={handleEFTClick}
            disabled={eftCreated}
            className={`py-2 px-6 rounded-md transition-colors ${
              eftCreated
                ? "bg-gray-400 text-gray-600 cursor-not-allowed"
                : "bg-gray-200 text-black hover:bg-gray-300"
            }`}
          >
            {eftCreated ? "EFT Created" : "E.F.T."}
          </button>
          <button
            type="button"
            onClick={onBack}
            className="bg-red-500 text-white py-2 px-6 rounded-md hover:bg-red-600 transition-colors"
          >
            Exit
          </button>
        </div>
      </div>
    );
  };

  const BuilderRepaymentDetailsForm = ({
    trade,
    builderName,
    suggestedRepaymentAmount,
    onBack,
    eftCreated,
  }) => {
    const formatSuggestedAmount = (value) => {
      const n = Number(value);
      if (!Number.isFinite(n) || n <= 0) return "";
      return n.toFixed(2);
    };

    const [formState, setFormState] = useState({
      chequeDate: getCurrentMDYDate(),
      bankAccount: "Commission Trust",
      tradeNumber: trade?.tradeNumber || "",
      builderName: builderName || "",
      builderStreetNumber: "",
      builderStreetName: "",
      builderUnit: "",
      builderCity: "",
      builderProvince: "",
      builderPostalCode: "",
      amount: formatSuggestedAmount(suggestedRepaymentAmount),
      noteOnStub: "Builder repayment",
    });
    const [useCustomDate, setUseCustomDate] = useState(false);

    useEffect(() => {
      if (trade) {
        const prefilled = formatSuggestedAmount(suggestedRepaymentAmount);
        setFormState((prev) => ({
          ...prev,
          tradeNumber: trade.tradeNumber,
          builderName: builderName || prev.builderName,
          amount: prefilled || prev.amount,
        }));
      }
    }, [trade, builderName, suggestedRepaymentAmount]);

    const handleInputChange = (e) => {
      const { name, value } = e.target;
      setFormState((prev) => ({ ...prev, [name]: value }));
    };

    const handleEFTClick = async () => {
      if (!trade) return;
      const amount = Number(formState.amount);
      if (!Number.isFinite(amount) || amount <= 0) {
        toast.error("Please enter a valid repayment amount.");
        return;
      }
      if (!formState.builderName?.trim()) {
        toast.error(
          "Builder name is required. Record Received From when finalizing the trade first.",
        );
        return;
      }

      const existingEFT = await checkCommissionTrustEFT(
        trade._id,
        "BuilderRepayment",
      );
      if (existingEFT.exists) {
        toast.error(
          `Builder repayment already completed for Trade #${trade.tradeNumber}!\nEFT #${existingEFT.eftNumber} was created on ${formatDate(existingEFT.date)}.`,
          { duration: 6000 },
        );
        setBuilderRepaymentEftCreated(true);
        return;
      }

      const tradeAddress = `${trade.keyInfo?.streetNumber || ""} ${
        trade.keyInfo?.streetName || ""
      }${trade.keyInfo?.unit ? ` ${trade.keyInfo.unit}` : ""}`.trim();
      const partyNames = getPartyNamesByDealType(trade);

      try {
        const eftResponse = await axiosInstance.post(
          "/commission-trust-eft/builder-repayment",
          {
            tradeId: trade._id,
            amount,
            builderName: formState.builderName.trim(),
            chequeDate: formState.chequeDate,
            notes: formState.noteOnStub,
          },
        );
        const eftNum = eftResponse.data.eftNumber;
        const paidTo =
          eftResponse.data.builderName || formState.builderName.trim();

        setEftReceiptData({
          eftNumber: eftNum,
          paidTo,
          trade: `${trade.tradeNumber} ${tradeAddress}`,
          notes: formState.noteOnStub || "Builder repayment",
          amount: String(amount),
          payTo: paidTo,
          orderOf: {
            streetNumber: formState.builderStreetNumber,
            streetName: formState.builderStreetName,
            unitNumber: formState.builderUnit,
            city: formState.builderCity,
            province: formState.builderProvince,
            postalCode: formState.builderPostalCode,
          },
          note: `${trade.tradeNumber} ${tradeAddress}`,
          chequeDate: formState.chequeDate,
          seller: partyNames.firstParty,
          buyer: partyNames.secondParty,
          firstPartyLabel: partyNames.firstPartyLabel,
          secondPartyLabel: partyNames.secondPartyLabel,
        });
        setIsOurBrokerageEFT(false);
        setShowEFTReceipt(true);
        setBuilderRepaymentEftCreated(true);
        const posting = eftResponse.data?.ledgerPosting;
        toast.success(
          posting
            ? `Builder repayment posted: Debit ${posting.debitAccount}, Credit ${posting.creditAccount} (EFT #${eftNum}).`
            : "Builder repayment posted to ledger and EFT created successfully!",
        );
      } catch (error) {
        console.error("Error creating builder repayment EFT:", error);
        toast.error(
          error.response?.data?.message ||
            "Failed to create builder repayment. Please try again.",
        );
      }
    };

    return (
      <div className="p-6 border border-gray-200 rounded-lg">
        <h3 className="font-semibold mb-4 text-lg">
          Builder Repayment Cheque Details
        </h3>
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Cheque Date
            </label>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="useCustomDateBuilder"
                checked={useCustomDate}
                onChange={(e) => setUseCustomDate(e.target.checked)}
                className="rounded border-gray-300"
              />
              <label
                htmlFor="useCustomDateBuilder"
                className="text-sm text-gray-600"
              >
                Override Date
              </label>
            </div>
            {useCustomDate ? (
              <input
                type="date"
                name="chequeDate"
                value={
                  formState.chequeDate.includes("/")
                    ? (() => {
                        const [month, day, year] =
                          formState.chequeDate.split("/");
                        return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
                      })()
                    : formState.chequeDate
                }
                onChange={(e) => {
                  const selectedDate = new Date(e.target.value + "T00:00:00");
                  const formattedDate = `${(selectedDate.getMonth() + 1)
                    .toString()
                    .padStart(2, "0")}/${selectedDate
                    .getDate()
                    .toString()
                    .padStart(2, "0")}/${selectedDate.getFullYear()}`;
                  setFormState((prev) => ({
                    ...prev,
                    chequeDate: formattedDate,
                  }));
                }}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
              />
            ) : (
              <input
                type="text"
                name="chequeDate"
                value={formState.chequeDate}
                readOnly
                className="mt-1 block w-full rounded-md border-gray-300 bg-gray-100 shadow-sm"
              />
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Bank Account
            </label>
            <input
              type="text"
              name="bankAccount"
              value={formState.bankAccount}
              readOnly
              className="mt-1 block w-full rounded-md border-gray-300 bg-gray-100 shadow-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Trade #
            </label>
            <input
              type="text"
              name="tradeNumber"
              value={formState.tradeNumber}
              readOnly
              className="mt-1 block w-full rounded-md border-gray-300 bg-gray-100 shadow-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Builder Name (finalize balance payment)
            </label>
            <input
              type="text"
              name="builderName"
              value={formState.builderName}
              readOnly
              className="mt-1 block w-full rounded-md border-gray-300 bg-gray-100 shadow-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Builder Street #
            </label>
            <input
              type="text"
              name="builderStreetNumber"
              value={formState.builderStreetNumber}
              onChange={handleInputChange}
              placeholder="Street #"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Builder Street Name
            </label>
            <input
              type="text"
              name="builderStreetName"
              value={formState.builderStreetName}
              onChange={handleInputChange}
              placeholder="Street name"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Builder Unit #
            </label>
            <input
              type="text"
              name="builderUnit"
              value={formState.builderUnit}
              onChange={handleInputChange}
              placeholder="Unit # (optional)"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Builder City
            </label>
            <input
              type="text"
              name="builderCity"
              value={formState.builderCity}
              onChange={handleInputChange}
              placeholder="City"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Builder Province
            </label>
            <input
              type="text"
              name="builderProvince"
              value={formState.builderProvince}
              onChange={handleInputChange}
              placeholder="Province"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Builder Postal Code
            </label>
            <input
              type="text"
              name="builderPostalCode"
              value={formState.builderPostalCode}
              onChange={handleInputChange}
              placeholder="Postal code"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Repayment Amount
            </label>
            <input
              type="number"
              name="amount"
              value={formState.amount}
              onChange={handleInputChange}
              min="0.01"
              step="0.01"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
              placeholder="Enter amount"
            />
            {suggestedRepaymentAmount != null &&
              Number(suggestedRepaymentAmount) > 0 && (
                <p className="mt-1 text-xs text-gray-500">
                  Pre-filled from finalize balance payment (
                  {formatSuggestedAmount(suggestedRepaymentAmount)}). You may
                  change this amount.
                </p>
              )}
          </div>
          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-700">
              Note on Stub
            </label>
            <input
              type="text"
              name="noteOnStub"
              value={formState.noteOnStub}
              onChange={handleInputChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
            />
          </div>
        </div>
        <div className="flex space-x-4">
          <button
            type="button"
            onClick={handleEFTClick}
            disabled={eftCreated}
            className={`py-2 px-6 rounded-md transition-colors ${
              eftCreated
                ? "bg-gray-400 text-gray-600 cursor-not-allowed"
                : "bg-gray-200 text-black hover:bg-gray-300"
            }`}
          >
            {eftCreated ? "EFT Created" : "E.F.T."}
          </button>
          <button
            type="button"
            onClick={onBack}
            className="bg-red-500 text-white py-2 px-6 rounded-md hover:bg-red-600 transition-colors"
          >
            Exit
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      <Navbar />
      <div className="flex">
        {/* Left Sidebar */}
        {/* Main Content */}
        <div className="flex-1 p-6">
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h2 className="text-2xl font-bold mb-4 text-gray-800">
              Commission Trust Payments
            </h2>

            {/* Secondary Horizontal Navbar */}
            <div className="bg-white py-4 border-b mb-6">
              <nav className="flex space-x-8">
                <button
                  className={`text-lg font-medium px-3 py-2 hover:text-blue-600 transition-colors ${
                    activeSection === "outside-brokers"
                      ? "text-blue-600 border-b-2 border-blue-600"
                      : "text-gray-500"
                  }`}
                  onClick={() => setActiveSection("outside-brokers")}
                >
                  Outside Brokers Commission Cheques
                </button>
                <button
                  className={`text-lg font-medium px-3 py-2 hover:text-blue-600 transition-colors ${
                    activeSection === "agent-commission"
                      ? "text-blue-600 border-b-2 border-blue-600"
                      : "text-gray-500"
                  }`}
                  onClick={() => setActiveSection("agent-commission")}
                >
                  Agent Commission Cheques
                </button>

                <button
                  className={`text-lg font-medium px-3 py-2 hover:text-blue-600 transition-colors ${
                    activeSection === "our-brokerage"
                      ? "text-blue-600 border-b-2 border-blue-600"
                      : "text-gray-500"
                  }`}
                  onClick={() => setActiveSection("our-brokerage")}
                >
                  Our Brokerage Commission
                </button>
                <button
                  className={`text-lg font-medium px-3 py-2 hover:text-blue-600 transition-colors ${
                    activeSection === "builder-repayment"
                      ? "text-blue-600 border-b-2 border-blue-600"
                      : "text-gray-500"
                  }`}
                  onClick={() => setActiveSection("builder-repayment")}
                >
                  Builder Repayment
                </button>
                <button
                  className={`text-lg font-medium px-3 py-2 hover:text-blue-600 transition-colors ${
                    activeSection === "general-to-commission"
                      ? "text-blue-600 border-b-2 border-blue-600"
                      : "text-gray-500"
                  }`}
                  onClick={() => setActiveSection("general-to-commission")}
                >
                  General to Commission
                </button>
              </nav>
            </div>

            {/* Outside Brokers Commission Cheques Form */}
            {activeSection === "outside-brokers" && (
              <div className="space-y-4">
                {!showOutsideBrokersDetailsForm ? (
                  <div className="p-6 border border-gray-200 rounded-lg">
                    <h3 className="font-semibold mb-4 text-lg">
                      Outside Brokers Commission Transfer Form
                    </h3>
                    <form
                      onSubmit={handleOutsideBrokersSubmit}
                      className="space-y-4"
                    >
                      {/* Name */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Name
                        </label>
                        <input
                          type="text"
                          value={outsideBrokersForm.name}
                          readOnly
                          className="block w-full rounded-md border-gray-300 bg-gray-100 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        />
                      </div>

                      {/* Type */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Type
                        </label>
                        <input
                          type="text"
                          value={outsideBrokersForm.type}
                          readOnly
                          className="block w-full rounded-md border-gray-300 bg-gray-100 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        />
                      </div>

                      {/* Trade Number Search */}
                      <div className="relative">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Trade Number
                        </label>
                        <input
                          type="text"
                          value={outsideBrokersForm.tradeNumber}
                          onChange={(e) =>
                            handleOutsideBrokersTradeSearch(e.target.value)
                          }
                          placeholder="Search trade number or address..."
                          className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        />

                        {/* Dropdown for trade search results */}
                        {showTradeDropdown && filteredTrades.length > 0 && (
                          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                            {filteredTrades.map((trade) => {
                              const address = `${
                                trade.keyInfo?.streetNumber || ""
                              } ${trade.keyInfo?.streetName || ""}${
                                trade.keyInfo?.unit
                                  ? ` ${trade.keyInfo.unit}`
                                  : ""
                              }`.trim();
                              return (
                                <div
                                  key={trade._id}
                                  className="px-4 py-2 hover:bg-gray-100 cursor-pointer border-b border-gray-200"
                                  onClick={() =>
                                    selectOutsideBrokersTrade(trade)
                                  }
                                >
                                  <div className="font-medium">
                                    Trade #{trade.tradeNumber}
                                  </div>
                                  <div className="text-sm text-gray-600">
                                    {address}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>

                      {/* Outside Broker Dropdown (includes referral agents from the trade) */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Outside Broker
                        </label>
                        <select
                          name="selectedOutsideBroker"
                          value={
                            outsideBrokersForm.selectedOutsideBroker
                              ? getOutsideBrokerKey(
                                  outsideBrokersForm.selectedOutsideBroker,
                                )
                              : ""
                          }
                          onChange={(e) => {
                            const selectedKey = e.target.value;
                            if (selectedKey) {
                              const combined = getOutsideBrokersWithReferrals(
                                outsideBrokersForm.selectedTrade,
                              );
                              const selectedBroker = combined.find(
                                (broker) =>
                                  getOutsideBrokerKey(broker) === selectedKey,
                              );
                              setOutsideBrokersForm((prev) => ({
                                ...prev,
                                selectedOutsideBroker: selectedBroker || null,
                              }));
                            } else {
                              setOutsideBrokersForm((prev) => ({
                                ...prev,
                                selectedOutsideBroker: null,
                              }));
                            }
                          }}
                          className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 disabled:bg-gray-100"
                          required
                          disabled={!outsideBrokersForm.selectedTrade}
                        >
                          <option value="" disabled>
                            {outsideBrokersForm.selectedTrade
                              ? "Select an outside broker"
                              : "Select a trade first"}
                          </option>
                          {outsideBrokersForm.selectedTrade &&
                            getOutsideBrokersWithReferrals(
                              outsideBrokersForm.selectedTrade,
                            ).map((broker, index) => (
                              <option
                                key={index}
                                value={getOutsideBrokerKey(broker)}
                              >
                                {broker.agentName ||
                                  `${broker.firstName || ""} ${broker.lastName || ""}`.trim()}{" "}
                                - {broker.company}
                              </option>
                            ))}
                        </select>
                      </div>

                      {/* Proceed Button */}
                      <div className="pt-4">
                        <button
                          type="submit"
                          className="bg-blue-600 text-white py-2 px-6 rounded-md hover:bg-blue-700 transition-colors"
                        >
                          Proceed
                        </button>
                      </div>
                    </form>
                  </div>
                ) : (
                  <OutsideBrokersCommissionDetailsForm
                    trade={outsideBrokersForm.selectedTrade}
                    selectedOutsideBroker={
                      outsideBrokersForm.selectedOutsideBroker
                    }
                    onBack={() => setShowOutsideBrokersDetailsForm(false)}
                    eftCreated={
                      outsideBrokersForm.selectedOutsideBroker
                        ? (eftCreated.outsideBrokersCommission &&
                            eftCreated.outsideBrokersCommission[
                              getOutsideBrokerKey(
                                outsideBrokersForm.selectedOutsideBroker,
                              )
                            ]) ||
                          false
                        : false
                    }
                    onEftCreated={() => {
                      const key = getOutsideBrokerKey(
                        outsideBrokersForm.selectedOutsideBroker,
                      );
                      if (!key) return;
                      setEftCreated((prev) => ({
                        ...prev,
                        outsideBrokersCommission: {
                          ...(prev.outsideBrokersCommission || {}),
                          [key]: true,
                        },
                      }));
                    }}
                  />
                )}
              </div>
            )}

            {/* Agent Commission Cheques Form */}
            {activeSection === "agent-commission" && (
              <div className="space-y-4">
                {!showDetailsForm ? (
                  <div className="p-6 border border-gray-200 rounded-lg">
                    <h3 className="font-semibold mb-4 text-lg">
                      Agent Commission Transfer Form
                    </h3>
                    <form
                      onSubmit={handleAgentCommissionSubmit}
                      className="space-y-4"
                    >
                      {/* Name */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Name
                        </label>
                        <input
                          type="text"
                          value={agentCommissionForm.name}
                          readOnly
                          className="block w-full rounded-md border-gray-300 bg-gray-100 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        />
                      </div>

                      {/* Cheque Type */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Cheque Type
                        </label>
                        <input
                          type="text"
                          value={agentCommissionForm.chequeType}
                          readOnly
                          className="block w-full rounded-md border-gray-300 bg-gray-100 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        />
                      </div>

                      {/* Trade Number Search */}
                      <div className="relative">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Trade Number
                        </label>
                        <input
                          type="text"
                          value={agentCommissionForm.tradeNumber}
                          onChange={(e) =>
                            handleAgentCommissionTradeSearch(e.target.value)
                          }
                          placeholder="Search trade number or address..."
                          className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        />

                        {/* Dropdown for trade search results */}
                        {showTradeDropdown && filteredTrades.length > 0 && (
                          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                            {filteredTrades.map((trade) => {
                              const address = `${
                                trade.keyInfo?.streetNumber || ""
                              } ${trade.keyInfo?.streetName || ""}${
                                trade.keyInfo?.unit
                                  ? ` ${trade.keyInfo.unit}`
                                  : ""
                              }`.trim();
                              return (
                                <div
                                  key={trade._id}
                                  className="px-4 py-2 hover:bg-gray-100 cursor-pointer border-b border-gray-200"
                                  onClick={() =>
                                    selectAgentCommissionTrade(trade)
                                  }
                                >
                                  <div className="font-medium">
                                    Trade #{trade.tradeNumber}
                                  </div>
                                  <div className="text-sm text-gray-600">
                                    {address}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>

                      {/* Agent Number Dropdown */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Agent
                        </label>
                        <select
                          name="selectedAgentId"
                          value={agentCommissionForm.selectedAgentId}
                          onChange={(e) =>
                            setAgentCommissionForm((prev) => ({
                              ...prev,
                              selectedAgentId: e.target.value,
                            }))
                          }
                          className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 disabled:bg-gray-100"
                          required
                          disabled={!agentCommissionForm.selectedTrade}
                        >
                          <option value="" disabled>
                            {agentCommissionForm.selectedTrade
                              ? "Select an agent"
                              : "Select a trade first"}
                          </option>
                          {(() => {
                            // Filter unique agents by agentId; exclude Referral (they use Outside Brokers Commission Cheques)
                            const uniqueAgents = [];
                            const seenAgentIds = new Set();

                            agentCommissionForm.selectedTrade?.agentCommissionList?.forEach(
                              (agent) => {
                                const isReferral =
                                  (agent.classification || "").toUpperCase() ===
                                  "REFERRAL";
                                if (
                                  !isReferral &&
                                  !seenAgentIds.has(agent.agentId)
                                ) {
                                  seenAgentIds.add(agent.agentId);
                                  uniqueAgents.push(agent);
                                }
                              },
                            );

                            return uniqueAgents.map((agent, index) => (
                              <option key={index} value={agent.agentId}>
                                {agent.agentName} ({agent.agentId})
                              </option>
                            ));
                          })()}
                        </select>
                      </div>

                      {/* Proceed Button */}
                      <div className="pt-4">
                        <button
                          type="submit"
                          className="bg-blue-600 text-white py-2 px-6 rounded-md hover:bg-blue-700 transition-colors"
                        >
                          Proceed
                        </button>
                      </div>
                    </form>
                  </div>
                ) : (
                  <AgentCommissionDetailsForm
                    trade={agentCommissionForm.selectedTrade}
                    selectedAgentId={agentCommissionForm.selectedAgentId}
                    onBack={() => setShowDetailsForm(false)}
                    eftCreatedForAgent={
                      eftCreated.agentCommission[
                        agentCommissionForm.selectedAgentId
                      ] || false
                    }
                  />
                )}
              </div>
            )}

            {/* Builder Repayment Section */}
            {activeSection === "builder-repayment" && (
              <div className="space-y-4">
                {!showBuilderRepaymentDetailsForm ? (
                  <div className="p-6 border border-gray-200 rounded-lg">
                    <h3 className="font-semibold mb-4 text-lg">
                      Builder Repayment Transfer Form
                    </h3>
                    <form
                      onSubmit={async (e) => {
                        e.preventDefault();
                        if (!builderRepaymentForm.selectedTrade) {
                          alert("Please select a trade to proceed.");
                          return;
                        }
                        if (!builderRepaymentForm.builderName?.trim()) {
                          toast.error(
                            "Builder name not found for this trade. Record Received From when finalizing the trade first.",
                          );
                          return;
                        }
                        const existingEFT = await checkCommissionTrustEFT(
                          builderRepaymentForm.selectedTrade._id,
                          "BuilderRepayment",
                        );
                        if (existingEFT.exists) {
                          toast.error(
                            `Builder repayment already completed for Trade #${builderRepaymentForm.selectedTrade.tradeNumber}!\nEFT #${existingEFT.eftNumber} was created on ${formatDate(existingEFT.date)}.`,
                            { duration: 6000 },
                          );
                          setBuilderRepaymentEftCreated(true);
                          return;
                        }
                        setShowBuilderRepaymentDetailsForm(true);
                      }}
                      className="space-y-4"
                    >
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Name
                        </label>
                        <input
                          type="text"
                          value={builderRepaymentForm.name}
                          readOnly
                          className="block w-full rounded-md border-gray-300 bg-gray-100 shadow-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Type
                        </label>
                        <input
                          type="text"
                          value={builderRepaymentForm.type}
                          readOnly
                          className="block w-full rounded-md border-gray-300 bg-gray-100 shadow-sm"
                        />
                      </div>
                      <div className="relative">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Trade Number
                        </label>
                        <input
                          type="text"
                          value={builderRepaymentForm.tradeNumber}
                          onChange={(e) =>
                            handleBuilderRepaymentTradeSearch(e.target.value)
                          }
                          placeholder="Search trade number or address..."
                          className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        />
                        {showTradeDropdown && filteredTrades.length > 0 && (
                          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                            {filteredTrades.map((trade) => {
                              const address = `${
                                trade.keyInfo?.streetNumber || ""
                              } ${trade.keyInfo?.streetName || ""}${
                                trade.keyInfo?.unit
                                  ? ` ${trade.keyInfo.unit}`
                                  : ""
                              }`.trim();
                              return (
                                <div
                                  key={trade._id}
                                  className="px-4 py-2 hover:bg-gray-100 cursor-pointer border-b border-gray-200"
                                  onClick={() =>
                                    selectBuilderRepaymentTrade(trade)
                                  }
                                >
                                  <div className="font-medium">
                                    Trade #{trade.tradeNumber}
                                  </div>
                                  <div className="text-sm text-gray-600">
                                    {address}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Builder Name (finalize balance payment — Received
                          From)
                        </label>
                        <input
                          type="text"
                          value={builderRepaymentForm.builderName}
                          readOnly
                          placeholder="Select a trade to load builder name"
                          className="block w-full rounded-md border-gray-300 bg-gray-100 shadow-sm"
                        />
                      </div>
                      <div className="pt-4">
                        <button
                          type="submit"
                          className="bg-blue-600 text-white py-2 px-6 rounded-md hover:bg-blue-700 transition-colors"
                        >
                          Proceed
                        </button>
                      </div>
                    </form>
                  </div>
                ) : (
                  <BuilderRepaymentDetailsForm
                    trade={builderRepaymentForm.selectedTrade}
                    builderName={builderRepaymentForm.builderName}
                    suggestedRepaymentAmount={
                      builderRepaymentForm.suggestedRepaymentAmount
                    }
                    onBack={() => setShowBuilderRepaymentDetailsForm(false)}
                    eftCreated={builderRepaymentEftCreated}
                  />
                )}
              </div>
            )}

            {/* General to Commission Transfer Section */}
            {activeSection === "general-to-commission" && (
              <div className="space-y-4">
                {!showGeneralToCommissionDetailsForm ? (
                  <div className="p-6 border border-gray-200 rounded-lg">
                    <h3 className="font-semibold mb-4 text-lg">
                      Commission Transfer — General to Commission Account
                    </h3>
                    <form
                      onSubmit={async (e) => {
                        e.preventDefault();
                        if (!generalToCommissionForm.selectedTrade) {
                          alert("Please select a trade to proceed.");
                          return;
                        }
                        const existingEFT = await checkCommissionTrustEFT(
                          generalToCommissionForm.selectedTrade._id,
                          "GeneralToCommissionTransfer",
                        );
                        if (existingEFT.exists) {
                          toast.error(
                            `General to Commission transfer already completed for Trade #${generalToCommissionForm.selectedTrade.tradeNumber}!\nEFT #${existingEFT.eftNumber} was created on ${formatDate(existingEFT.date)}.`,
                            { duration: 6000 },
                          );
                          setGeneralToCommissionEftCreated(true);
                          return;
                        }
                        setShowGeneralToCommissionDetailsForm(true);
                      }}
                      className="space-y-4"
                    >
                      <div className="relative">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Trade Number
                        </label>
                        <input
                          type="text"
                          value={generalToCommissionForm.tradeNumber}
                          onChange={(e) =>
                            handleGeneralToCommissionTradeSearch(e.target.value)
                          }
                          placeholder="Search trade number or address..."
                          className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        />
                        {showTradeDropdown && filteredTrades.length > 0 && (
                          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                            {filteredTrades.map((trade) => {
                              const address = `${
                                trade.keyInfo?.streetNumber || ""
                              } ${trade.keyInfo?.streetName || ""}${
                                trade.keyInfo?.unit
                                  ? ` ${trade.keyInfo.unit}`
                                  : ""
                              }`.trim();
                              return (
                                <div
                                  key={trade._id}
                                  className="px-4 py-2 hover:bg-gray-100 cursor-pointer border-b border-gray-200"
                                  onClick={() =>
                                    selectGeneralToCommissionTrade(trade)
                                  }
                                >
                                  <div className="font-medium">
                                    Trade #{trade.tradeNumber}
                                  </div>
                                  <div className="text-sm text-gray-600">
                                    {address}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                      <div className="pt-4">
                        <button
                          type="submit"
                          className="bg-blue-600 text-white py-2 px-6 rounded-md hover:bg-blue-700 transition-colors"
                        >
                          Proceed
                        </button>
                      </div>
                    </form>
                  </div>
                ) : (
                  <GeneralToCommissionDetailsForm
                    trade={generalToCommissionForm.selectedTrade}
                    onBack={() => setShowGeneralToCommissionDetailsForm(false)}
                    eftCreated={generalToCommissionEftCreated}
                  />
                )}
              </div>
            )}

            {/* Our Brokerage Commission Section */}
            {activeSection === "our-brokerage" && (
              <div className="space-y-4">
                {!showOurBrokerageDetailsForm ? (
                  <div className="p-6 border border-gray-200 rounded-lg">
                    <h3 className="font-semibold mb-4 text-lg">
                      Our Brokerage Commission Transfer Form
                    </h3>
                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        if (ourBrokerageForm.selectedTrade) {
                          setShowOurBrokerageDetailsForm(true);
                        } else {
                          alert("Please select a trade to proceed.");
                        }
                      }}
                      className="space-y-4"
                    >
                      {/* Trade Number Search */}
                      <div className="relative">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Trade Number
                        </label>
                        <input
                          type="text"
                          value={ourBrokerageForm.tradeNumber}
                          onChange={(e) =>
                            handleOurBrokerageTradeSearch(e.target.value)
                          }
                          placeholder="Search trade number or address..."
                          className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        />
                        {/* Dropdown for trade search results */}
                        {showTradeDropdown && filteredTrades.length > 0 && (
                          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                            {filteredTrades.map((trade) => {
                              const address = `${
                                trade.keyInfo?.streetNumber || ""
                              } ${trade.keyInfo?.streetName || ""}${
                                trade.keyInfo?.unit
                                  ? ` ${trade.keyInfo.unit}`
                                  : ""
                              }`.trim();
                              return (
                                <div
                                  key={trade._id}
                                  className="px-4 py-2 hover:bg-gray-100 cursor-pointer border-b border-gray-200"
                                  onClick={() => selectOurBrokerageTrade(trade)}
                                >
                                  <div className="font-medium">
                                    Trade #{trade.tradeNumber}
                                  </div>
                                  <div className="text-sm text-gray-600">
                                    {address}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                      {/* Proceed Button */}
                      <div className="pt-4">
                        <button
                          type="submit"
                          className="bg-blue-600 text-white py-2 px-6 rounded-md hover:bg-blue-700 transition-colors"
                        >
                          Proceed
                        </button>
                      </div>
                    </form>
                  </div>
                ) : (
                  <OurBrokerageCommissionDetailsForm
                    trade={ourBrokerageForm.selectedTrade}
                    onBack={() => setShowOurBrokerageDetailsForm(false)}
                    eftCreated={eftCreated.ourBrokerageCommission}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* EFT Receipt Modal */}
      {showEFTReceipt && eftReceiptData && (
        <EFTReceipt
          data={eftReceiptData}
          hideTradeSellerBuyer={isOurBrokerageEFT}
          onClose={() => {
            setShowEFTReceipt(false);
            setEftReceiptData(null);
            setEftNumber(null);
            setIsOurBrokerageEFT(false);
          }}
        />
      )}

      {/* EFT Receipt Modal for Outside Brokers Commission Cheques */}
      {showOutsideBrokersReceipt && outsideBrokersReceiptData && (
        <EFTReceipt
          data={outsideBrokersReceiptData}
          onClose={() => {
            setShowOutsideBrokersReceipt(false);
            setOutsideBrokersReceiptData(null);
          }}
        />
      )}
    </div>
  );
};

export default CommissionTrustPayments;
